import compact from 'lodash/fp/compact';
import flow from 'lodash/fp/flow';
import map from 'lodash/fp/map';
import trim from 'lodash/fp/trim';
import join from 'lodash/fp/join';
import split from 'lodash/fp/split';
import size from 'lodash/fp/size';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';

interface TextAreaFormItemProps {
    values: string[];
    label: string;
    placeholder?: string;
    placeholderId?: string;
    disabled?: boolean;
    validate: (values: string[]) => boolean;
    onChange: (data: { values: string[]; isValid: boolean }) => void;
    errorMessage?: string;
}

const TextAreaFormItem: React.FC<TextAreaFormItemProps> = ({
    values,
    label,
    placeholder,
    disabled = false,
    validate,
    onChange,
    errorMessage = 'intl-msg:invalidInput'
}) => {
    const intl = useIntl();
    const [value, setValue] = useState(join(', ', values));
    const [isValid, setIsValid] = useState(validate(values));
    const [focused, setFocused] = useState(false);

    const formatPlaceholder = intl.formatMessage({ id: placeholder });

    useEffect(() => {
        if (!focused) {
            setValue(join(', ', values));
            setIsValid(validate(values));
        }
    }, [values, focused, validate]);

    const handleFocus = () => {
        setFocused(true);
    };

    const handleBlur = () => {
        setFocused(false);
    };

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = event.target.value;
        const parsedValues = flow(
            split(','),
            map(item => trim(item)),
            compact,
        )(newValue);
        const valid = validate(parsedValues);
        setValue(newValue);
        setIsValid(valid);
        onChange({ values: parsedValues, isValid: valid });
    };

    const helpBlockMessage = !isValid ?
        <FormattedMessage id={errorMessage}/> : undefined;

    return (
        <div className={classNames('form-group', { 'has-error': !isValid })}>
            <label className='control-label'>
                <FormattedMessage id={label}/>
                <span className='margin-left-10'/>
                <span className='badge bg-info'>
                    <FormattedNumber value={size(values)}/>
                </span>
            </label>
            <textarea
                className={classNames('form-control', { disabled })}
                value={value}
                placeholder={formatPlaceholder}
                style={{ height: '100px' }}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onChange={handleChange}
            />
            <span className='help-block'>{helpBlockMessage}</span>
        </div>
    );
};

export default TextAreaFormItem;
